import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { ActivityType } from "../types/activity";
import {
  ActivityLog,
  PerformanceLog,
  Session,
  TrainingLogLinks,
} from "../types/postActivity";
import { GlobalActivityService } from "./globalActivityService";

export async function getTrainingLogLinks(
  userId: string,
  logId: string,
): Promise<TrainingLogLinks | null> {
  const snap = await getDoc(doc(db, "trainingLogs", userId, "logs", logId));
  if (!snap.exists()) return null;
  const data = snap.data();
  return {
    linkedActivityLogID: data.linkedActivityLogID as string | undefined,
    linkedSessionID: data.linkedSessionID as string | undefined,
  };
}

export async function getActivityLog(
  userId: string,
  logId: string,
): Promise<ActivityLog | null> {
  const snap = await getDoc(doc(db, "activityLogs", userId, "logs", logId));
  if (!snap.exists()) return null;
  return snap.data() as ActivityLog;
}

export async function getSession(
  userId: string,
  sessionId: string,
): Promise<(Session & { id: string }) | null> {
  const snap = await getDoc(
    doc(db, "sessions", userId, "sessions", sessionId),
  );
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Session) };
}

export async function getPerformanceLog(
  userId: string,
  logId: string,
): Promise<PerformanceLog | null> {
  const snap = await getDoc(doc(db, "performanceLogs", userId, "logs", logId));
  if (!snap.exists()) return null;
  return snap.data() as PerformanceLog;
}

export async function fetchUserActivityTypes(
  userId: string,
): Promise<ActivityType[]> {
  const typesRef = collection(db, "activityTypes", userId, "types");
  const snap = await getDocs(query(typesRef, orderBy("name")));
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<ActivityType, "id">) }))
    .filter((type) => !type.deleted);
}

export async function recoverUserActivityType(
  userId: string,
  activityTypeId: string,
): Promise<ActivityType | null> {
  const snap = await getDoc(
    doc(db, "activityTypes", userId, "types", activityTypeId),
  );
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<ActivityType, "id">) };
}

export async function recoverGlobalActivityType(
  categoryId: string,
  activityTypeId: string,
): Promise<ActivityType | null> {
  const snap = await getDoc(
    doc(db, "adminActivityTypes", categoryId, "types", activityTypeId),
  );
  if (!snap.exists()) return null;
  const data = snap.data() as Omit<ActivityType, "id">;
  return {
    id: snap.id,
    ...data,
    isFromPack: true,
    categoryId,
  };
}

export async function fetchGlobalTypesByCategory(
  categoryId: string,
): Promise<ActivityType[]> {
  const types = await GlobalActivityService.fetchActivityTypes(categoryId);
  return types.map((type) => ({
    id: type.id,
    name: type.name,
    description: type.description,
    variables: type.variables,
    image: type.image,
    isFromPack: true,
    categoryId: type.categoryId || categoryId,
  }));
}

export async function fetchAllGlobalTypesGrouped(): Promise<
  Record<string, ActivityType[]>
> {
  const categories = await GlobalActivityService.fetchCategories();
  const categoryIds = categories
    .map((c) => c.id)
    .filter((id): id is string => !!id);
  if (categoryIds.length === 0) return {};

  const allTypes =
    await GlobalActivityService.fetchActivityTypesForCategories(categoryIds);

  return allTypes.reduce<Record<string, ActivityType[]>>((acc, type) => {
    if (!type.categoryId) return acc;
    if (!acc[type.categoryId]) acc[type.categoryId] = [];
    acc[type.categoryId].push({
      id: type.id,
      name: type.name,
      description: type.description,
      variables: type.variables,
      image: type.image,
      isFromPack: true,
      categoryId: type.categoryId,
    });
    return acc;
  }, {});
}
